import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Image from '../AppImage';

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const navigationLinks = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: 'Products', path: '/products' },
    { label: 'Contact', path: '/contact' }
  ];

  const productCategories = [
    'Horticultural Products',
    'Agricultural Commodities', 
    'Oil Seeds & Derivatives',
    'Processed Products',
    'Spices & Herbs'
  ];

  const contactInfo = [
    {
      icon: 'MapPin',
      label: 'Address',
      value: 'Abuja, Nigeria',
      link: null
    },
    {
      icon: 'Mail',
      label: 'Email',
      value: 'exquisitecoralafrica@gmail.com',
      link: 'mailto:exquisitecoralafrica@gmail.com'
    },
    {
      icon: 'MessageCircle',
      label: 'WhatsApp',
      value: 'Chat with us',
      link: 'https://wa.me/2347061704302'
    }
  ];

  const socialLinks = [
    {
      icon: 'Linkedin',
      label: 'LinkedIn',
      url: '#',
      color: 'hover:text-blue-600'
    },
    {
      icon: 'Twitter',
      label: 'Twitter',
      url: '#',
      color: 'hover:text-blue-400'
    },
    {
      icon: 'Facebook',
      label: 'Facebook',
      url: '#',
      color: 'hover:text-blue-700'
    },
    {
      icon: 'Instagram',
      label: 'Instagram',
      url: '#',
      color: 'hover:text-pink-600'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContactClick = (link) => {
    if (link) {
      if (link.startsWith('http')) {
        window.open(link, '_blank', 'noopener noreferrer');
      } else {
        window.open(link, '_self');
      }
    }
  };

  return (
    <footer className="bg-gradient-to-br from-charcoal-800 to-charcoal-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16 lg:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12">
            
            {/* Company Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="sm:col-span-2 lg:col-span-1"
            >
              {/* Logo and Company Name */}
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-10 w-auto flex items-center justify-center">
                  <Image
                    src="/assets/images/logo.png"
                    alt="Exquisite Coral Africa Limited Logo"
                    className="h-full w-auto object-contain max-w-none"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Exquisite Coral Africa</h3>
                  <p className="text-sm text-white/70">Premium Export Solutions</p>
                </div>
              </div>

              <p className="text-white/80 mb-4 leading-relaxed text-sm lg:text-base">
                An agricultural export company dedicated to bringing premium African agricultural products 
                to international markets through value-added processing and exceptional quality standards.
              </p>

              {/* Social Media Links */}
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-2.5 bg-white/10 rounded-lg text-white/70 transition-all duration-300 hover:bg-white/20 ${social.color}`}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.label}
                  >
                    <Icon name={social.icon} size={16} />
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Quick Navigation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="hidden sm:block"
            >
              <h4 className="text-lg font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {navigationLinks.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => handleNavigation(link.path)}
                      className="text-white/70 hover:text-white transition-colors duration-300 flex items-center space-x-2 group text-sm"
                    >
                      <Icon name="ChevronRight" size={12} className="group-hover:translate-x-1 transition-transform duration-300" />
                      <span>{link.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Product Categories */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <h4 className="text-lg font-semibold text-white mb-4">Our Products</h4>
              <ul className="space-y-2">
                {productCategories.map((product, index) => (
                  <li key={index}>
                    <button
                      onClick={() => handleNavigation('/products')}
                      className="text-white/70 hover:text-white transition-colors duration-300 flex items-center space-x-2 group text-left"
                    >
                      <Icon name="Package" size={12} className="text-primary group-hover:text-secondary transition-colors duration-300" />
                      <span className="text-sm">{product}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h4 className="text-lg font-semibold text-white mb-4">Get In Touch</h4>
              <ul className="space-y-3">
                {contactInfo.map((contact, index) => (
                  <li key={index}>
                    {contact.link ? (
                      <button
                        onClick={() => handleContactClick(contact.link)}
                        className="flex items-start space-x-2 text-white/70 hover:text-white transition-colors duration-300 group text-left w-full"
                      >
                        <Icon 
                          name={contact.icon} 
                          size={14} 
                          className="text-primary group-hover:text-secondary transition-colors duration-300 mt-0.5 flex-shrink-0" 
                        />
                        <div>
                          <div className="text-xs font-medium text-white/90">{contact.label}</div>
                          <div className="text-sm">{contact.value}</div>
                        </div>
                      </button>
                    ) : (
                      <div className="flex items-start space-x-2 text-white/70">
                        <Icon 
                          name={contact.icon} 
                          size={14} 
                          className="text-primary mt-0.5 flex-shrink-0" 
                        />
                        <div>
                          <div className="text-xs font-medium text-white/90">{contact.label}</div>
                          <div className="text-sm">{contact.value}</div>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <motion.button
                onClick={() => handleNavigation('/contact')}
                className="mt-4 w-full bg-primary hover:bg-primary-700 text-white py-2.5 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl text-sm"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon name="MessageCircle" size={16} />
                <span>Start Export Journey</span>
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
              
              {/* Copyright */}
              <div className="text-white/60 text-xs sm:text-sm text-center sm:text-left">
                <p>© {currentYear} Exquisite Coral Africa Limited. All rights reserved.</p>
              </div>

              {/* Legal Links - Hidden on mobile */}
              <div className="hidden sm:flex items-center space-x-4 lg:space-x-6 text-sm">
                <button 
                  onClick={() => handleNavigation('/privacy')}
                  className="text-white/60 hover:text-white transition-colors duration-300"
                >
                  Privacy Policy
                </button>
                <button 
                  onClick={() => handleNavigation('/terms')}
                  className="text-white/60 hover:text-white transition-colors duration-300"
                >
                  Terms of Service
                </button>
              </div>

              {/* Made in Nigeria Badge */}
              <div className="flex items-center space-x-2 text-white/60 text-xs sm:text-sm">
                <span>🇳🇬</span>
                <span>Made in Nigeria</span>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-full -translate-x-16 -translate-y-16" />
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-gradient-to-tl from-secondary/10 to-transparent rounded-full translate-x-24 translate-y-24" />
      </div>
    </footer>
  );
};

export default Footer;